import type { ReactNode } from 'react'
import { useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useAuth } from '@/hooks/useAuth'
import { Avatar } from '@/components/ui/Avatar'
import { ProfileDialog } from '@/components/domain/ProfileDialog'
import { defaultAvatarColor, isAvatarColorName } from '@/lib/avatarColors'
import { cn } from '@/lib/cn'
import { formatIST } from '@/lib/datetime'
import {
  ChevronDown,
  LogOut,
  User,
  ICON_SIZE,
  ICON_STROKE,
  type LucideIcon,
} from '@/components/icons'

/**
 * Top navigation user dropdown menu.
 */
export function UserMenu() {
  const { user, session, signOut } = useAuth()
  const [profileOpen, setProfileOpen] = useState(false)

  if (!user) return null

  const avatarColor =
    user.avatar_color && isAvatarColorName(user.avatar_color)
      ? user.avatar_color
      : defaultAvatarColor(user.id)

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          className={cn(
            'flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors duration-150',
            'hover:bg-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
          )}
        >
          <Avatar name={user.name} size="sm" color={avatarColor} />
          <span className="hidden min-w-0 flex-col leading-tight sm:flex">
            <span className="truncate text-sm font-medium text-text">{user.name}</span>
            <span className="truncate text-xs text-subtle">{user.role.name}</span>
          </span>
          <ChevronDown size={ICON_SIZE.xs} strokeWidth={ICON_STROKE} aria-hidden className="text-subtle" />
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            className="z-50 min-w-56 rounded-md border border-line bg-surface p-1 shadow-overlay"
          >
            <div className="flex flex-col gap-0.5 px-2 py-2">
              <p className="truncate text-sm font-medium text-text">{user.name}</p>
              <p className="truncate text-xs text-subtle">{user.email}</p>
              {session && (
                <p className="mt-1 text-xs text-subtle">
                  Signed out at {formatIST(session.idle_expires_at, 'time')}
                </p>
              )}
            </div>

            <DropdownMenu.Separator className="my-1 h-px bg-line" />

            <MenuItem icon={User} onSelect={() => { setProfileOpen(true) }}>
              Profile
            </MenuItem>
            <MenuItem icon={LogOut} onSelect={() => { void signOut() }}>
              Sign out
            </MenuItem>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <ProfileDialog open={profileOpen} onClose={() => { setProfileOpen(false) }} />
    </>
  )
}

function MenuItem({
  icon: Icon,
  onSelect,
  children,
}: {
  icon: LucideIcon
  onSelect: () => void
  children: ReactNode
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-base text-muted',
        'outline-none select-none',
        'data-[highlighted]:bg-hover data-[highlighted]:text-text',
      )}
    >
      <Icon size={ICON_SIZE.sm} strokeWidth={ICON_STROKE} aria-hidden />
      {children}
    </DropdownMenu.Item>
  )
}
